import {Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {filter, map, switchMap, take, withLatestFrom} from 'rxjs/operators';
import {BehaviorSubject, forkJoin, Observable, of} from 'rxjs';
import {OAuthService} from 'angular-oauth2-oidc';
import {ActivatedRouteSnapshot, CanActivate, CanActivateChild, RouterStateSnapshot, UrlTree} from '@angular/router';
import {UserModel} from '../user/user-list/user.model';
import {DesktopMapKeys} from '../map-settings/map-settings-types';
import {ProjectBusinessLogicFilterService} from '../shared/project-business-logic-filter/project-business-logic-filter.service';

@Injectable(
  {
    providedIn: 'root'
  }
)
export class AuthorizationService implements CanActivate, CanActivateChild {
  public static readonly MODERATOR_TOKEN_NAME = 'moderator_token';

  constructor(
    private oauthService: OAuthService,
    private http: HttpClient,
    private projectBusinessLogicFilterService: ProjectBusinessLogicFilterService,
  ) { }

  public static readonly ACTION_CREATE = 'create';
  public static readonly ACTION_READ = 'read';
  public static readonly ACTION_LIST = 'list';
  public static readonly ACTION_UPDATE = 'update';
  public static readonly ACTION_DELETE = 'delete';
  public static readonly ROLE_ROOT = 'root';
  public static readonly ROLE_SUPERUSER = 'superuser';
  public static readonly ROLE_ADMIN = 'admin';
  public static readonly ROLE_INTEGRATOR = 'integrator';
  public static readonly ROLE_MANAGER = 'manager';
  public static readonly ROLE_TECHNICIAN = 'technician';
  public static readonly ROLE_DISPATCHER = 'dispatcher';

  public static readonly CALCULATED_ROLE_ROOT = AuthorizationService.ROLE_ROOT;
  public static readonly CALCULATED_ROLE_SUPERUSER = AuthorizationService.ROLE_SUPERUSER;
  public static readonly CALCULATED_ROLE_ADMIN = AuthorizationService.ROLE_ADMIN;
  public static readonly CALCULATED_ROLE_INTEGRATOR = AuthorizationService.ROLE_INTEGRATOR;
  public static readonly CALCULATED_ROLE_ADMINS_MANAGER = 'admins-manager';
  public static readonly CALCULATED_ROLE_INTEGRATORS_MANAGER = 'integrators-manager';
  public static readonly CALCULATED_ROLE_ADMINS_DISPATCHER = 'admins-dispatcher';
  public static readonly CALCULATED_ROLE_INTEGRATORS_DISPATCHER = 'integrators-dispatcher';
  public static readonly CALCULATED_ROLE_ADMINS_TECHNICIAN = 'admins-technician';
  public static readonly CALCULATED_ROLE_INTEGRATORS_TECHNICIAN = 'integrators-technician';

  public static readonly ENTITY_USER = 'user';
  public static readonly ENTITY_CURRENT_USER = 'current-user';
  public static readonly ENTITY_UNIT_HW_TYPE = 'unit-hw-type';
  public static readonly ENTITY_UNIT_COMMAND_NAME = 'unit-command-name';
  public static readonly ENTITY_UNIT_COMMAND_TEXT = 'unit-command-text';
  public static readonly ENTITY_UNIT_COMMAND_DESCRIPTION_PER_PROJECT = 'unit-command-description-per-project';
  public static readonly ENTITY_UNIT_COMMAND_DESCRIPTION_PER_ADMIN = 'unit-command-description-per-admin';
  public static readonly ENTITY_UNIT_HW_MODEL = 'unit-hw-model';
  public static readonly ENTITY_UNIT = 'unit';
  public static readonly ENTITY_UNIT_SHOW_IN_APPLICATION = 'unit-show-in-application';
  public static readonly ENTITY_END_USER_PRICE_PLAN = 'end-user-price-plan';
  public static readonly ENTITY_END_USER_BALANCE_TRANSACTION = 'end-user-balance-transaction';
  public static readonly ENTITY_PROJECT = 'project';
  public static readonly ENTITY_PROJECT_COUNTRY = 'project-country';
  public static readonly ENTITY_SUPPORT_CONTACT = 'support-contact';
  public static readonly ENTITY_REGION = 'region';
  public static readonly ENTITY_UNIT_MODEL = 'unit-model';
  public static readonly ENTITY_UNIT_MODEL_GROUP = 'unit-model-group';
  public static readonly ENTITY_END_USER = 'end-user';
  public static readonly ENTITY_END_USER_SEND_PUSH_BATCH = 'end-user-send-push-batch';
  public static readonly ENTITY_PROJECT_SEND_PUSH_BATCH = 'project-send-push-batch';
  public static readonly ENTITY_RENT_BOOKING = 'rent-booking';
  public static readonly ENTITY_END_USER_ORDER = 'end-user-order';
  public static readonly ENTITY_COUNTRY_ACQUIRER = 'acquirer';
  public static readonly ENTITY_SMS_GATEWAY = 'sms-gateway';
  public static readonly ENTITY_GEOFENCE = 'geofence';
  public static readonly ENTITY_GEOPOINT = 'geopoint';
  public static readonly ENTITY_GEOPOINT_TYPE = 'geopoint-type';
  public static readonly ENTITY_PROMO_CODE = 'promo-code';
  public static readonly ENTITY_SERVICE_REQUEST = 'service-request';
  public static readonly ENTITY_END_USER_ORDER_TRANSACTION = 'end-user-order-transaction';
  public static readonly ENTITY_UNIT_COMMAND_BATCH = 'unit-command-batch';
  public static readonly ENTITY_NOTIFICATION = 'notification';
  public static readonly ENTITY_UNIT_LOG = 'unit-log';
  public static readonly ENTITY_USER_LOG = 'user-log';
  public static readonly ENTITY_UNIT_COMMENT = 'unit-comment';
  public static readonly ENTITY_UNIT_MESSAGE = 'unit-message';
  public static readonly ENTITY_ACCEPTANCE_CERTIFICATE = 'acceptance-certificate';
  public static readonly ENTITY_STATISTIC = 'user-statistic';
  public static readonly ENTITY_BATTERY = 'battery';
  public static readonly ENTITY_USER_TASK_SETTINGS = 'user-tasks-setting';
  public static readonly ENTITY_DEBT_REPAYMENT_LOG = 'debt-repayment-log';
  public static readonly ENTITY_END_USER_EXTENDED_REGISTRATION = 'end-user-extended-registration';
  public static readonly ENTITY_END_USER_SUBSCRIPTION = 'end-user-subscription';
  public static readonly ENTITY_END_USER_PROMO_CODE = 'end-user-promo-code';
  public static readonly ENTITY_BALANCE_PACKAGE_PURCHASE = 'balance-package-purchase';
  public static readonly ENTITY_TOURIST_ROUTE_PURCHASE = 'tourist-route-purchase';
  public static readonly ENTITY_FINANCIAL_OPERATION = 'financial-operation';
  public static readonly ENTITY_END_USER_LOG = 'end-user-log';
  public static readonly ENTITY_END_USER_COMMENT = 'end-user-comment';
  public static readonly ENTITY_USER_PRICE_PLAN = 'user-price-plan';
  public static readonly ENTITY_USER_BALANCE_TOP_UP = 'user-balance-top-up';
  public static readonly ENTITY_CHANGE_END_USER_PRICE_PLAN_AVAILABILITY = 'change-end-user-price-plan-availability';
  public static readonly ENTITY_CHANGE_SUBSCRIPTION_AVAILABILITY = 'change-subscription-availability';
  public static readonly ENTITY_UNIT_HEAT_MAP = 'unit-heat-map';

  public static calculatedRoleHierarchy = [
    AuthorizationService.CALCULATED_ROLE_ROOT,
    AuthorizationService.CALCULATED_ROLE_SUPERUSER,
    AuthorizationService.CALCULATED_ROLE_ADMIN,
    AuthorizationService.CALCULATED_ROLE_ADMINS_MANAGER,
    AuthorizationService.CALCULATED_ROLE_INTEGRATOR,
    AuthorizationService.CALCULATED_ROLE_INTEGRATORS_MANAGER,
    AuthorizationService.CALCULATED_ROLE_ADMINS_DISPATCHER,
    AuthorizationService.CALCULATED_ROLE_ADMINS_TECHNICIAN,
    AuthorizationService.CALCULATED_ROLE_INTEGRATORS_DISPATCHER,
    AuthorizationService.CALCULATED_ROLE_INTEGRATORS_TECHNICIAN,
  ];

  public static readonly BUSINESS_LOGIC_TYPE_SHARING = 'sharing';
  public static readonly BUSINESS_LOGIC_TYPE_RENTAL = 'rental';
  public static readonly BUSINESS_LOGIC_TYPE_COURIER = 'courier';
  public static readonly BUSINESS_LOGIC_TYPE_CHARGING = 'charging';
  public static readonly BUSINESS_LOGIC_TYPE_TAXI = 'taxi';
  public static readonly BUSINESS_LOGIC_TYPE_QR_SHARING = 'qr sharing';

  public identity: BehaviorSubject<UserModel> = new BehaviorSubject<UserModel>(null);
  public temporaryAccessRights: BehaviorSubject<any> = new BehaviorSubject<null>(null);
  public desktopMapKeys: BehaviorSubject<DesktopMapKeys | null> = new BehaviorSubject<DesktopMapKeys | null>(null);
  public moderatorToken: string | null = null;

  public static userRoleIsExactlyAdmin(user: UserModel) {
    return AuthorizationService.roleIsExactlyAdmin(user.role);
  }
  public static userRoleIsExactlyIntegrator(user: UserModel) {
    return AuthorizationService.roleIsExactlyIntegrator(user.role);
  }
  public static roleIsExactlyAdmin(role: string) {
    return role === AuthorizationService.ROLE_ADMIN;
  }
  public static roleIsExactlyIntegrator(role: string) {
    return role === AuthorizationService.ROLE_INTEGRATOR;
  }

  public static calculatedRoleHasRightsOfExactlyIntegrator(user: UserModel) {
    return user.calculatedRole === AuthorizationService.CALCULATED_ROLE_INTEGRATOR
      || user.calculatedRole === AuthorizationService.CALCULATED_ROLE_INTEGRATORS_MANAGER;
  }

  public static roleIsExactlyManager(role: string) {
    return role === AuthorizationService.ROLE_MANAGER;
  }
  public static roleIsExactlyDispatcher(role: string) {
    return role === AuthorizationService.ROLE_DISPATCHER;
  }
  public static roleIsExactlyTechnician(role: string) {
    return role === AuthorizationService.ROLE_TECHNICIAN;
  }
  public static hasRightsNonLessThan(user: UserModel, role: string): boolean {
    const roleHierarchy = [
      AuthorizationService.ROLE_ROOT,
      AuthorizationService.ROLE_SUPERUSER,
      AuthorizationService.ROLE_ADMIN,
      AuthorizationService.ROLE_INTEGRATOR,
      AuthorizationService.ROLE_MANAGER,
      AuthorizationService.ROLE_DISPATCHER,
      AuthorizationService.ROLE_TECHNICIAN,
    ];
    const identityRoleIndex = roleHierarchy.indexOf(user.role);
    if (identityRoleIndex === -1) { return false; }
    const requestedRoleIndex = roleHierarchy.indexOf(role);
    return requestedRoleIndex !== -1 && identityRoleIndex <= requestedRoleIndex;
  }

  public static calculatedRoleHasRightsNonLessThanSuperuser(user: UserModel) {
    return AuthorizationService.userCalculatedRoleHasRightsNonLessThan(user, AuthorizationService.CALCULATED_ROLE_SUPERUSER);
  }
  public static calculatedRoleHasRightsNonLessThanAdminsManager(user: UserModel) {
    return AuthorizationService.userCalculatedRoleHasRightsNonLessThan(user, AuthorizationService.CALCULATED_ROLE_ADMINS_MANAGER);
  }
  public static calculatedRoleHasRightsNonLessThanIntegratorsManager(user: UserModel) {
    return AuthorizationService.userCalculatedRoleHasRightsNonLessThan(user, AuthorizationService.CALCULATED_ROLE_INTEGRATORS_MANAGER);
  }
  public static userCalculatedRoleHasRightsNonLessThan(user: UserModel, calculatedRole: string): boolean {
    const userCalculatedRole = user.calculatedRole;
    const userCalculatedRoleIndex = AuthorizationService.calculatedRoleHierarchy.indexOf(userCalculatedRole);
    if (userCalculatedRoleIndex === -1) { return false; }
    const requestedRoleIndex = AuthorizationService.calculatedRoleHierarchy.indexOf(calculatedRole);
    return requestedRoleIndex !== -1 && userCalculatedRoleIndex <= requestedRoleIndex;
  }
  public static userCalculatedRoleHasRightsMoreThan(user: UserModel, calculatedRole: string): boolean {
    const userCalculatedRole = user.calculatedRole;
    const userCalculatedRoleIndex = AuthorizationService.calculatedRoleHierarchy.indexOf(userCalculatedRole);
    if (userCalculatedRoleIndex === -1) { return false; }
    const requestedRoleIndex = AuthorizationService.calculatedRoleHierarchy.indexOf(calculatedRole);
    return requestedRoleIndex !== -1 && userCalculatedRoleIndex < requestedRoleIndex;
  }

  public static formatRoleAsText(role: string) {
    const en = {
      root: 'Root',
      superuser: 'Superuser',
      admin: 'Administrator',
      integrator: 'Integrator',
      manager: 'Manager',
      dispatcher: 'Dispatcher',
      technician: 'Technician',
    };
    return en[role] || '';
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot)
    : Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // console.log({route: route, state: state});
    if (route.data.modelName) {
      // console.log(
      //   `Checking access for modelName ${route.data.modelName}, action ${route.data.action}`
      //   + (route.params.id ? `, id ${route.params.id}` : '')
      // );
      return this.checkAccess(route.data.modelName, route.data.action, route.params.id);
    } else {
      // console.log('Model name not specified in route config - allowing this route');
    }
    return true;
    // return this.identity.pipe(
    //   filter(
    //     (identity: UserModel) => !!identity
    //   ),
    //   map(
    //     (identity: UserModel) => {
    //       return this.router.parseUrl('/' + this.firstAvailableMainRoutePath(identity));
    //     }
    //   )
    // );
//    const urlTree = this.router.parseUrl('/company');
//    return urlTree;
  }
  checkAccessFromModel(model: any, action: string): boolean {
    if (model.model_access_rights) {
      // console.log('Model has access rights');
      return !!model.model_access_rights[action];
    } else {
      // console.log('Model does not have access_rights');
      return true;
    }
  }
  canCancelInsurance(): Observable<boolean> {
    return this.getEntityAccessRights().pipe(
      map(
        entityAccessRights => {
          return !!(entityAccessRights['insurance-cancel'] && entityAccessRights['insurance-cancel'].create);
        }
      )
    );
  }


  canAccessCourierFeatures(): Observable<boolean> {
    return this.projectBusinessLogicFilterService.selectedBusinessLogics.pipe(
      map(bls => bls !== null ?
        bls.some(bl => bl.type === AuthorizationService.BUSINESS_LOGIC_TYPE_COURIER)
        : false
      )
    );
  }

  canAccessSharingFeatures(): Observable<boolean> {
    return this.projectBusinessLogicFilterService.selectedBusinessLogics.pipe(
      map(bls => bls !== null ?
        bls.some(bl => bl.type === AuthorizationService.BUSINESS_LOGIC_TYPE_SHARING)
        : false
      )
    );
  }
  canAccessQrSharingFeatures(): Observable<boolean> {
    return this.projectBusinessLogicFilterService.selectedBusinessLogics.pipe(
      map(bls => bls !== null ?
        bls.some(bl => bl.type === AuthorizationService.BUSINESS_LOGIC_TYPE_QR_SHARING)
        : false
      )
    );
  }

  unitTypeMenuVisibility(unitTypeName: string): Observable<boolean> {
    return this.projectBusinessLogicFilterService.selectedBusinessLogics.pipe(
      map(
        bls => {
          // console.log(bl);
          return bls !== null ?
            bls.some(bl => (unitTypeName in bl.unitTypeNameIndex) && bl.unitTypeNameIndex[unitTypeName])
            : true;
        }
      )
    );
  }

  checkAccess(modelName: string, action: string, id?: string, model?: any): Observable<boolean> {
    return this.getEntityAccessRights().pipe(
      withLatestFrom(this.identity),
      switchMap(
        ([entityAccessRights, identity]) => {
          if (modelName === AuthorizationService.ENTITY_USER
            && action === AuthorizationService.ACTION_UPDATE
            && Number(identity.id) === Number(id)
          ) {
            console.log('model = AuthorizationService.ENTITY_CURRENT_USER;');
            modelName = AuthorizationService.ENTITY_CURRENT_USER;
          }
          const entityRights = modelName in entityAccessRights ? entityAccessRights[modelName] : {};
          if (!!entityRights[action] || (action === 'read' && !!entityRights['list'])) {
            if (action === 'list' || action === 'create') {
              // console.log('Action found in access list - allowing this route');
              return of(true);
            } else {
              // console.log('Action found in access list - requesting model to check its access_rights field');
              if (typeof model !== 'undefined') {
                return of(this.checkAccessFromModel(model, action));
              }
              if (!id) {
                return of(true);
              }
              return this.http.get(
                environment.apiBasepoint + modelName + '/' + id + '?fields=id,admin_id,integrator_id,model_access_rights'
              ).pipe(
                // tap((response) => console.log({msg: 'Received model', model: response})),
                map(receivedModel => {
                  // console.log(model);
                  return this.checkAccessFromModel(receivedModel, action);
                })
              );
            }
          }
          return of(false);
        }
      ),
      take(1),
      // tap(result => console.log({afterSwitchMap: result})),
    );
  }

  public getAccessRights(): Observable<any> {
    return this.identity.pipe(
      filter(identity => !!identity),
      withLatestFrom(this.temporaryAccessRights),
      map(
        ([identity, temporaryAccessRights]) => temporaryAccessRights ? temporaryAccessRights : identity.access_rights
      )
    );
  }
  public getAccessRightsInstant(): any {
    if (!this.identity.getValue()) {
      return null;
    }
    return this.temporaryAccessRights.getValue() ? this.temporaryAccessRights.getValue() : this.identity.getValue().access_rights;
  }

  public getLoggedInUserCalculatedRoleInstant(): string {
    const identity = this.identity.getValue();
    if (!identity) {
      return '';
    }
    return identity.calculatedRole;
  }
  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return true;
  }
  public getEntityAccessRightsInstant(): any {
    const accessRights = this.getAccessRightsInstant();
    return accessRights ? accessRights.entities : null;
  }
  public getEntityAccessRights(): Observable<any> {
    return this.getAccessRights().pipe(
      filter(accessRights => !!accessRights),
      map(accessRights => accessRights.entities)
    );
  }
  public getLogin(): string {
    return this.identity.value && this.identity.value.login ? this.identity.value.login : '';
  }
  public getIdentity() {
    return this.identity.getValue();
  }
  public getIdentityObservable() {
    return this.identity;
  }

  public isAuthenticated(): boolean {
    const token = this.getToken();
    if (token) {
      return true;
    } else {
      return false;
    }
  }

  public requestMapSettings() {
    const httpParams = new HttpParams()
      .append(
        'expand',
        [
          'desktopMapKeys',
        ].join(',')
      );
    this.http.get<UserModel>(
      environment.apiBasepoint + 'user/' + this.getUserId(),
      {params: httpParams}
    ).subscribe(response => {
      this.desktopMapKeys.next(response.desktopMapKeys);
    });
  }

  private temporaryAccessRightsObservable(): Observable<any> {
    const httpParams = new HttpParams()
      .append('fields', 'id,access_rights')
      .append('expand', 'access_rights')
    ;
    return this.http.get<UserModel>(
      environment.apiBasepoint + 'user/' + this.getUserId(),
      {params: httpParams}
    ).pipe(
      map(response => response.access_rights)
    );
  }
  private fetchTemporaryAccessRights() {
    this.temporaryAccessRightsObservable().subscribe(
      (accessRights) => {
        this.temporaryAccessRights.next(accessRights);
      }
    );
  }

  private fetchModeratorTokenFromStorage() {
    const moderatorTokenFromLocalStorage = localStorage.getItem(AuthorizationService.MODERATOR_TOKEN_NAME);
    if (moderatorTokenFromLocalStorage) {
      this.moderatorToken = moderatorTokenFromLocalStorage;
    }
  }

  public requestIdentity() {
    this.fetchModeratorTokenFromStorage();
    const moderatorToken = this.getModeratorToken(); // check for expiration and user id
    const httpParams = new HttpParams()
      .append(
        'expand',
        [
          'access_rights',
          'geoBounds',
          'regions',
          'billingVer202212Status',
          'userFinanceAcquirerCurrencyCode',
          'integrator',
          'showCourierFeatures',
          'showSharingFeatures',
          'showQrSharingFeatures',
          'unitTypeVisibility',
          'simsBalanceCurrencyAbbreviation',
        ].join(',')
      );
    this.http.get<UserModel>(
      environment.apiBasepoint + 'user/' + this.getUserId(),
      {params: httpParams}
    ).pipe(
      switchMap(
        identity => {
          if (moderatorToken) {
            return forkJoin(
              of(identity),
              this.temporaryAccessRightsObservable()
            );
          } else {
            return forkJoin(of(identity), of(null));
          }
        }
      )
    ).subscribe(
      ([identity, temporaryAccessRights]) => {
        if (temporaryAccessRights) {
          this.temporaryAccessRights.next(temporaryAccessRights);
        }
        this.identity.next(identity);
      }
    );
  }


  public hasNextAcquirer(): Observable<boolean> {
    return this.getIdentityObservable().pipe(
      filter(user => !!user),
      map(user =>
        !!(
          (user.acquirer_id && user.next_acquirer_id)
          || (user.integrator && user.integrator.acquirer_id && user.integrator.next_acquirer_id)
        )
      )
    );
  }

  public canTopUpUserBalanceUsingBankCard(user: UserModel): Observable<boolean> {
    return this.getIdentityObservable().pipe(
      filter(loggedInUser => !!loggedInUser),
      map(loggedInUser => {
        const result = !!user.billingVer202212Status
          && !!user.user_billing_acquirer_id
          && (user.role === AuthorizationService.ROLE_ADMIN || user.role === AuthorizationService.ROLE_INTEGRATOR)
          && (
            user.id === loggedInUser.id
            || (
              loggedInUser.calculatedRole === AuthorizationService.CALCULATED_ROLE_ADMINS_MANAGER
                && loggedInUser.admin_id === user.id
            )
            || (
              loggedInUser.calculatedRole === AuthorizationService.CALCULATED_ROLE_INTEGRATORS_MANAGER
                && loggedInUser.integrator_id === user.id
            )
          );
        // console.log({canTopUpBalanceUsingBankCard: result});
        return result;
      })
    );
  }

  protected getToken() {
    return this.oauthService.getAccessToken();
  }

  protected getUserId() {
    const token = this.getToken();
    if (!token) {
      return null;
    }
    const tokenParts = token.split('.');
    const payload = JSON.parse(atob(tokenParts[1]));
    return parseInt(payload.jti, 10);
  }

  public isRoot() {
    return this.getUserId() === 1;
  }

  public isRootOrSuperuser() {
    return this.getIdentityObservable().pipe(
      filter(
        identity => !!identity
      ),
      switchMap(
        identity => of(identity.role === AuthorizationService.ROLE_ROOT
          || identity.role === AuthorizationService.ROLE_SUPERUSER)
      )
    );
  }

  public canCreatePricePlans() {
    return this.getEntityAccessRights().pipe(
      map(
        entityAccessRights => !!entityAccessRights['end-user-price-plan'].create
      )
    );
  }


  public canCreateExecutorPricePlans() {
    return this.getEntityAccessRights().pipe(
      map(
        entityAccessRights => !!entityAccessRights['executor-price-plan'].create
      )
    );
  }

  public canBroadcastPushMessagesToRegion() {
    return this.getEntityAccessRights().pipe(
      map(
        entityAccessRights => !!entityAccessRights['region-push-broadcast'].create
      )
    );
  }


  loggedInUserHasRightsNonLessThanSuperuser(): Observable<boolean> {
    return this.identity.pipe(
      map(
        user => AuthorizationService.hasRightsNonLessThan(user, AuthorizationService.ROLE_SUPERUSER)
      )
    );
  }
  userHasLessRightsThanLoggedInUser(user: UserModel): Observable<boolean> {
    return this.identity.pipe(
      map(
        loggedInUser => AuthorizationService.userCalculatedRoleHasRightsMoreThan(loggedInUser, user.calculatedRole)
      )
    );
  }
  loggedInUserHasRightsNonLessThanAdminsManager(): Observable<boolean> {
    return this.identity.pipe(
      map(
        user => AuthorizationService.calculatedRoleHasRightsNonLessThanAdminsManager(user)
      )
    );
  }
  loggedInUserHasRightsNonLessThanIntegratorsManager(): Observable<boolean> {
    return this.identity.pipe(
      map(
        user => AuthorizationService.calculatedRoleHasRightsNonLessThanIntegratorsManager(user)
      )
    );
  }
  loggedInUserHasRightsNonLessThanDispatcher(): Observable<boolean> {
    return this.identity.pipe(
      map(
        user => AuthorizationService.hasRightsNonLessThan(user, AuthorizationService.ROLE_DISPATCHER)
      )
    );
  }
  loggedInUserHasRightsOfExactlyIntegrator(): Observable<boolean> {
    return this.identity.pipe(
      map(
        user =>
          user.calculatedRole === AuthorizationService.CALCULATED_ROLE_INTEGRATOR
          || user.calculatedRole === AuthorizationService.CALCULATED_ROLE_INTEGRATORS_MANAGER
      )
    );
  }

  storeModeratorToken(token: string): void {
    this.moderatorToken = token;
    localStorage.setItem(AuthorizationService.MODERATOR_TOKEN_NAME, token);
    if (token) {
      this.fetchTemporaryAccessRights();
    }
  }

  removeModeratorToken() {
    this.moderatorToken = null;
    localStorage.removeItem(AuthorizationService.MODERATOR_TOKEN_NAME);
  }

  getModeratorToken(): string | null {
    if (!this.moderatorToken) {
      return null;
    }
    const payload = JSON.parse(atob(this.moderatorToken.split('.')[1]));
    const now = Math.floor(Date.now() / 1000);
    const expire = payload.exp;
    const tokenIsExpired = now >= expire;
    const userId = payload.jti;
    if (tokenIsExpired || this.getUserId() !== parseInt(userId, 10)) {
      this.moderatorToken = null;
      localStorage.removeItem(AuthorizationService.MODERATOR_TOKEN_NAME);
      this.temporaryAccessRights.next(null);
      return null;
    }
    return this.moderatorToken;
  }

  logout() {
    this.storeModeratorToken(null);
    this.moderatorToken = null;
    localStorage.removeItem(AuthorizationService.MODERATOR_TOKEN_NAME);
    this.temporaryAccessRights.next(null);
    this.oauthService.logOut();
  }
}
